/* Turbo Parts — garage-aware search.
 * Goal: once we know the visitor's car (My Garage OR a car they just entered in the
 * finder / vehicle search), a plain part search like "brake pads" shows ONLY that car's
 * brake pads — not every car's. Mechanism: the native multi-tag collection URL
 *   /collections/all/mm-<car>+pt-<type>
 * which is a true server-side intersection. pt-<type> tags come from apply_parts_tags.py;
 * this script resolves the typed part to that pt-slug and does the redirect.
 *
 * Active car is set whenever the visitor is on a fitment collection (every entry path —
 * finder, vehicle search, garage click — lands there), stored per-session so it clears
 * when they leave. "Show all" links carry ?tpall=1 to bypass scoping for one search.
 */
(function () {
  var ACTIVE = 'tp-active-car';
  var partsMap = null;
  var labels = null;

  function norm(s) { return (s || '').toLowerCase().replace(/\s+/g, ' ').trim(); }
  function enc(s) { return encodeURIComponent(s); }
  function param(k) { try { return new URLSearchParams(location.search).get(k); } catch (e) { return null; } }

  function slug(s) {
    return (s || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-{2,}/g, '-').replace(/^-|-$/g, '');
  }
  // Build a fitment tag + label from the user's explicitly-chosen car ('tp-my-car',
  // the same object the YOUR CAR bar / finder writes). Mirrors tp-garage.liquid tagUrl.
  function fromMyCar(v) {
    if (!v || !v.make) return null;
    var mk = slug(v.make), md = slug(v.model), tag;
    // Scope to the deepest level the customer actually chose. Series (mms-) tags are now
    // derived from year data across the whole catalogue (~91% coverage), so scoping to the
    // generation (VE vs VF vs ZB) is both accurate AND safe — a VE search no longer shows
    // VF/ZB parts, and nothing that genuinely fits gets hidden. Falls back to model when no
    // series was picked. Label keeps the full car for display.
    if (mk && md && v.series) tag = 'mms-' + mk + '-' + md + '-' + slug(v.series);
    else if (mk && md) tag = 'mm-' + mk + '-' + md;
    else if (mk) tag = 'make-' + mk;
    else return null;
    var label = [v.make, v.model, v.series, v.year].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
    return { tag: tag, label: label };
  }
  // Reduce any fitment tag to its reliably-populated MODEL-level tag for scoping.
  function modelTag(tag) {
    if (/^(mm|make)-/.test(tag)) return tag;
    var yr = tag.match(/^yr-(.+)-\d{4}$/);
    if (yr) return 'mm-' + yr[1];
    var ms = tag.match(/^mms-(.+)-[a-z0-9]+$/);
    if (ms) return 'mm-' + ms[1];
    return tag;
  }
  function readActive() {
    // 1. the car the user actually PICKED (YOUR CAR bar / finder) wins — this is what
    //    they see as "their car", so search must scope to it, not a car they browsed.
    var my = null;
    try { my = JSON.parse(localStorage.getItem('tp-my-car') || 'null'); } catch (e) {}
    var fromPicked = fromMyCar(my);
    if (fromPicked) return fromPicked;
    // 2. else (visitor who searched a vehicle but never picked) the last browsed car.
    try { return JSON.parse(sessionStorage.getItem(ACTIVE) || 'null'); } catch (e) { return null; }
  }
  function writeActive(o) {
    try { sessionStorage.setItem(ACTIVE, JSON.stringify(o)); } catch (e) {}
  }

  // ---- resolve a typed part term to a pt-<slug> --------------------------------
  function loadParts(cb) {
    if (partsMap) { cb(); return; }
    if (!window.tpPartsMapUrl) return;
    fetch(window.tpPartsMapUrl)
      .then(function (r) { return r.json(); })
      .then(function (d) { partsMap = d || {}; cb(); })
      .catch(function () {});
  }

  var PART_FILLER = { for: 1, my: 1, the: 1, a: 1, new: 1, front: 1, rear: 1, left: 1,
                      right: 1, genuine: 1, oem: 1, aftermarket: 1, parts: 1, part: 1 };

  // If a search names a DIFFERENT make ("toyota corolla brake pads" while a Commodore is
  // active), don't hijack it to the active car — let normal search handle it. Makes that
  // double as part words (tank, ram, smart, mini, seat) are deliberately excluded.
  var MAKES = { alfa: 1, audi: 1, bmw: 1, buick: 1, byd: 1, cadillac: 1, chery: 1,
    chevrolet: 1, chrysler: 1, citroen: 1, dacia: 1, daewoo: 1, daihatsu: 1, dodge: 1,
    fiat: 1, ford: 1, fpv: 1, geely: 1, genesis: 1, gmc: 1, haval: 1, hillman: 1, hino: 1,
    holden: 1, honda: 1, hsv: 1, hummer: 1, hyundai: 1, infiniti: 1, isuzu: 1, iveco: 1,
    jac: 1, jaguar: 1, jeep: 1, kia: 1, lancia: 1, ldv: 1, lexus: 1, leyland: 1, mazda: 1,
    'mercedes-benz': 1, mercedes: 1, mg: 1, mitsubishi: 1, nissan: 1, omoda: 1, opel: 1,
    peugeot: 1, pontiac: 1, porsche: 1, proton: 1, renault: 1, rover: 1, saab: 1, skoda: 1,
    ssangyong: 1, subaru: 1, suzuki: 1, tesla: 1, toyota: 1, triumph: 1, volkswagen: 1,
    vw: 1, volvo: 1, holdens: 1 };
  // The makes above deliberately exclude words that double as part terms — mini, seat,
  // smart, ram, tank, great wall. But as a make+model PHRASE they clearly name a vehicle
  // ("mini cooper injector"), so a search for one shouldn't be hijacked to the active car
  // (which produced the nonsensical "No 'mini cooper injector' for your Ford Falcon BA").
  // These phrases are specific enough not to catch "mini fuse" / "seat cover" / "fuel tank".
  var MAKE_PHRASES = ['mini cooper', 'mini clubman', 'mini countryman', 'mini hatch',
    'mini one', 'mini paceman', 'mini roadster', 'mini coupe', 'mini convertible', 'mini jcw',
    'seat leon', 'seat ibiza', 'seat ateca', 'seat arona', 'seat alhambra', 'seat cordoba',
    'seat toledo', 'seat altea', 'seat exeo', 'seat mii', 'seat tarraco',
    'smart fortwo', 'smart forfour', 'smart roadster', 'smart cabrio',
    'ram 1500', 'ram 2500', 'ram 3500', 'great wall', 'land rover'];
  function namesAMake(q) {
    var nq = norm(q);
    for (var p = 0; p < MAKE_PHRASES.length; p++) {
      if (nq.indexOf(MAKE_PHRASES[p]) >= 0) return true;
    }
    var words = nq.split(' ');
    for (var i = 0; i < words.length; i++) { if (MAKES[words[i]]) return true; }
    return false;
  }

  function resolvePart(q) {
    if (!partsMap || !q) return null;
    var syn = partsMap.synonyms || {};
    var kw = partsMap.keywords || {};
    q = norm(q);
    if (syn[q]) return syn[q];
    var words = q.split(' ').filter(function (w) { return !PART_FILLER[w]; });
    // exact synonym on the meaningful phrase
    var phrase = words.join(' ');
    if (syn[phrase]) return syn[phrase];
    // any contiguous sub-phrase that's a known synonym (longest first)
    for (var len = words.length; len >= 1; len--) {
      for (var i = 0; i + len <= words.length; i++) {
        var sub = words.slice(i, i + len).join(' ');
        if (syn[sub]) return syn[sub];
      }
    }
    // token-overlap fallback against each type's keywords
    var best = null, bestScore = 0;
    for (var slug in kw) {
      var score = 0;
      for (var j = 0; j < words.length; j++) {
        if (kw[slug].indexOf(words[j]) >= 0) score++;
      }
      if (score > bestScore) { bestScore = score; best = slug; }
    }
    return bestScore >= 1 ? best : null;   // need at least one solid word match
  }

  // Universal parts (studs, bulbs, filters, fuses, spark plugs…) are sold by spec, not
  // by model — car-scoping them gives a false "nothing for your car". Show ALL of the
  // type instead, with a "sold by size" note.
  function isUniversal(slug) {
    return !!(partsMap && partsMap.universal && partsMap.universal.indexOf(slug) >= 0);
  }
  function scopedUrl(active, slug, q) {
    if (isUniversal(slug)) {
      return '/collections/all/' + slug + '?tppart=' + enc(q) + '&tpuniv=1&fit=' + enc(active.label);
    }
    return '/collections/all/' + active.tag + '+' + slug + '?fit=' + enc(active.label) + '&tppart=' + enc(q);
  }

  // ---- 1. remember the active car on fitment collection pages ------------------
  function setActiveFromPage(after) {
    var m = location.pathname.match(/\/collections\/[^/]+\/((?:mm|mms|yr|make)-[a-z0-9-]+)/i);
    if (!m) { if (after) after(); return; }
    var pageTag = m[1].toLowerCase();
    var tag = pageTag;                     // keep the page's fitment level (series/year now well-tagged)
    var fit = param('fit');
    if (fit) { writeActive({ tag: tag, label: fit.replace(/\s+/g, ' ').trim() }); if (after) after(); return; }
    // no ?fit (e.g. finder at year/series level) — build a clean label from the labels file
    if (labels) { writeActive({ tag: tag, label: labelForTag(pageTag) }); if (after) after(); return; }
    if (!window.tpFitLabelsUrl) { writeActive({ tag: tag, label: tidy(pageTag) }); if (after) after(); return; }
    fetch(window.tpFitLabelsUrl)
      .then(function (r) { return r.json(); })
      .then(function (d) { labels = (d && d.labels) || {}; writeActive({ tag: tag, label: labelForTag(pageTag) }); })
      .catch(function () { writeActive({ tag: tag, label: tidy(pageTag) }); })
      .then(function () { if (after) after(); });
  }
  // Clean label for any fitment tag. yr-/mms- tags reuse their base mm- label from the
  // labels file plus the year/series ("BMW 3 Series" + "2011"), so we never surface the
  // raw "Yr Bmw 3 Series 2011" tag text.
  function labelForTag(tag) {
    if (labels && labels[tag]) return labels[tag];               // mm-/make- direct hit
    var yr = tag.match(/^yr-(.+)-(\d{4})$/);
    if (yr && labels && labels['mm-' + yr[1]]) return labels['mm-' + yr[1]] + ' ' + yr[2];
    var ms = tag.match(/^mms-(.+)-([a-z0-9]+)$/);
    if (ms && labels && labels['mm-' + ms[1]]) return labels['mm-' + ms[1]] + ' ' + ms[2].toUpperCase();
    return tidy(tag);
  }
  var INITIALISMS = { Bmw: 'BMW', Byd: 'BYD', Ldv: 'LDV', Mg: 'MG', Hsv: 'HSV',
                      Fpv: 'FPV', Gmc: 'GMC', Jac: 'JAC', Vw: 'VW', Bmc: 'BMC' };
  function tidy(tag) {
    return tag.replace(/^(mms|mm|yr|make)-/, '').replace(/-/g, ' ')
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); })
      .replace(/\b(Bmw|Byd|Ldv|Mg|Hsv|Fpv|Gmc|Jac|Vw|Bmc)\b/g, function (w) { return INITIALISMS[w] || w; });
  }

  // ---- 2. scope a part search to the active car --------------------------------
  function maybeScopeSearch() {
    if (location.pathname.indexOf('/search') !== 0) return;
    if (param('tpall')) return;                 // user chose "show all"
    var active = readActive();
    if (!active || !active.tag) return;
    var q = param('q');
    if (!q || !norm(q)) return;
    if (namesAMake(q)) return;                  // query names a car — don't hijack to active
    loadParts(function () {
      var slug = resolvePart(q);
      if (!slug) return;                        // not a recognised part -> leave native search
      location.replace(scopedUrl(active, slug, q));
    });
  }

  // ---- 3. banner + empty-state on a scoped collection page ---------------------
  function ensureStyle() {
    if (document.getElementById('tp-gs-style')) return;
    var st = document.createElement('style');
    st.id = 'tp-gs-style';
    st.textContent =
      '.tp-scope-banner{max-width:var(--page-width,1400px);margin:8px auto 0;padding:11px 16px;' +
      'display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;' +
      'background:#17263F;border-radius:12px;color:#FBF6E9;font-family:Manrope,sans-serif;}' +
      '.tp-scope-banner__txt{font-size:13.5px;font-weight:700;}' +
      '.tp-scope-banner__txt span{font-weight:600;opacity:.8;}' +
      '.tp-scope-banner__all{flex-shrink:0;color:#FBF6E9;font-size:12.5px;font-weight:700;' +
      'text-decoration:underline;text-underline-offset:3px;white-space:nowrap;}' +
      '.tp-scope-empty{max-width:640px;margin:26px auto;padding:26px 22px;text-align:center;' +
      'background:#FBF6E9;border:1.5px solid #E0D6BF;border-radius:16px;font-family:Manrope,sans-serif;}' +
      '.tp-scope-empty h2{font-size:18px;font-weight:800;color:#17263F;margin:0 0 6px;}' +
      '.tp-scope-empty p{font-size:14px;color:rgba(23,38,63,.7);margin:0 0 16px;line-height:1.5;}' +
      '.tp-scope-empty a{display:inline-block;margin:0 5px;background:#CF3B2D;color:#FBF6E9;' +
      'border-radius:999px;padding:10px 18px;font-size:13px;font-weight:700;text-decoration:none;}' +
      '.tp-scope-empty a.ghost{background:transparent;color:#17263F;border:1.5px solid #17263F;}';
    document.head.appendChild(st);
  }

  function scopedPageUI() {
    var tppart = param('tppart');
    var universal = !!param('tpuniv');
    var isScoped = /\/collections\/[^/]+\/(?:mm|mms|yr|make)-[a-z0-9-]+\+pt-/i.test(location.pathname);
    if (!tppart || (!isScoped && !universal)) return;
    ensureStyle();
    var label = param('fit') || (readActive() || {}).label || 'your car';
    var term = tppart.replace(/</g, '');
    var allUrl = '/search?q=' + enc(tppart) + '&tpall=1';

    function hasProducts() { return !!document.querySelector('product-card, .product-card'); }

    function render() {
      if (document.getElementById('tp-scope-banner')) return;
      var host = document.querySelector('.collection, main, #MainContent, [id*="Collection"]') || document.body;
      if (universal) {
        // universal part — all shown; explain it's chosen by size, not model
        var u = document.createElement('div');
        u.id = 'tp-scope-banner';
        u.className = 'tp-scope-banner';
        u.innerHTML = '<span class="tp-scope-banner__txt">“' + term + '” fit by size, not model — ' +
          'showing all. <span>Check the size that suits your ' + label + '.</span></span>';
        host.insertBefore(u, host.firstChild);
      } else if (hasProducts()) {
        var b = document.createElement('div');
        b.id = 'tp-scope-banner';
        b.className = 'tp-scope-banner';
        b.innerHTML = '<span class="tp-scope-banner__txt">Showing “' + term + '” for your <span>' +
          label + '</span></span><a class="tp-scope-banner__all" href="' + allUrl +
          '">Show all “' + term + '” instead &rsaquo;</a>';
        host.insertBefore(b, host.firstChild);
      } else {
        var e = document.createElement('div');
        e.id = 'tp-scope-banner';
        e.className = 'tp-scope-empty';
        e.innerHTML = '<h2>No “' + term + '” listed for your ' + label + ' yet</h2>' +
          '<p>We might still be able to source it, or it may be listed under another name.</p>' +
          '<a href="' + allUrl + '">See all “' + term + '”</a>' +
          '<a class="ghost" href="/pages/request-a-part">Request this part</a>';
        host.insertBefore(e, host.firstChild);
      }
    }

    render();
    // grids can render late — retry briefly if nothing was there yet
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      var banner = document.getElementById('tp-scope-banner');
      if (banner && banner.className === 'tp-scope-empty' && hasProducts()) { banner.remove(); render(); }
      if (!banner) render();
      if (tries > 8) clearInterval(iv);
    }, 250);
  }

  // ---- instant Enter for part queries (skip the /search round-trip) ------------
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || e.isComposing) return;
    var inp = e.target;
    if (!inp || !inp.closest || !inp.closest('predictive-search-component') || inp.tagName !== 'INPUT') return;
    var active = readActive();
    if (!active || !active.tag || !partsMap) return;   // only when we can resolve instantly
    if (namesAMake(inp.value)) return;                 // query names a car — let it through
    var slug = resolvePart(inp.value);
    if (!slug) return;                                  // let vehicle handler / native search run
    e.preventDefault();
    e.stopPropagation();
    location.assign(scopedUrl(active, slug, norm(inp.value)));
  }, true);
  // warm the parts map when they focus search, so Enter can resolve instantly
  document.addEventListener('focusin', function (e) {
    if (e.target && e.target.closest && e.target.closest('predictive-search-component')) loadParts(function () {});
  }, true);

  function boot() {
    setActiveFromPage(maybeScopeSearch);
    scopedPageUI();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
})();
